import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui-kit/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui-kit/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui-kit/dropdown-menu';
import { Loader2, MoreVertical, Pencil, Star, Trash2, Check, Move } from 'lucide-react';
import type {
  OrganizationLetterhead,
  OrganizationLetterheadUpdateInput,
} from '../types/org-letterheads.types';
import type { UseMutationResult } from '@tanstack/react-query';

interface LetterheadCardProps {
  letterhead: OrganizationLetterhead;
  orgId: string;
  onUpdate: UseMutationResult<
    any,
    Error,
    { itemId: string; input: OrganizationLetterheadUpdateInput }
  >;
  onDelete: UseMutationResult<any, Error, string>;
}

export const LetterheadCard = ({ letterhead, onUpdate, onDelete }: LetterheadCardProps) => {
  const { t } = useTranslation();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showContentAreaDialog, setShowContentAreaDialog] = useState(false);
  const [editName, setEditName] = useState(letterhead.Name);

  // Content area editor state
  const [contentArea, setContentArea] = useState({
    x: letterhead.ContentAreaX,
    y: letterhead.ContentAreaY,
    width: letterhead.ContentAreaWidth,
    height: letterhead.ContentAreaHeight,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragHandle, setDragHandle] = useState<string | null>(null);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialArea, setInitialArea] = useState(contentArea);
  const imageContainerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [imageLoaded, setImageLoaded] = useState(false);

  const isUpdating = onUpdate.isPending;
  const isDeleting = onDelete.isPending;

  // Calculate scale for preview — viewport-aware so the page-sized image
  // doesn't overflow on phones. Re-runs when the dialog opens or window
  // resizes (e.g. orientation flip on tablet).
  useEffect(() => {
    if (!showContentAreaDialog) return;
    const computeScale = () => {
      const maxWidth = Math.min(500, window.innerWidth - 80);
      const maxHeight = Math.min(600, window.innerHeight - 280);
      const scaleX = maxWidth / letterhead.PageWidth;
      const scaleY = maxHeight / letterhead.PageHeight;
      setScale(Math.min(scaleX, scaleY, 1));
    };
    computeScale();
    window.addEventListener('resize', computeScale);
    return () => window.removeEventListener('resize', computeScale);
  }, [showContentAreaDialog, letterhead.PageWidth, letterhead.PageHeight]);

  // Reset content area when dialog opens
  useEffect(() => {
    if (showContentAreaDialog) {
      setContentArea({
        x: letterhead.ContentAreaX,
        y: letterhead.ContentAreaY,
        width: letterhead.ContentAreaWidth,
        height: letterhead.ContentAreaHeight,
      });
      setImageLoaded(false);
    }
  }, [showContentAreaDialog, letterhead]);

  const handleDelete = () => {
    onDelete.mutate(letterhead.ItemId, {
      onSuccess: () => setShowDeleteDialog(false),
    });
  };

  const handleSetDefault = () => {
    if (letterhead.IsDefault) return;
    onUpdate.mutate({ itemId: letterhead.ItemId, input: { IsDefault: true } });
  };

  const handleUpdateName = () => {
    if (!editName.trim() || editName.trim() === letterhead.Name) {
      setShowEditDialog(false);
      return;
    }
    onUpdate.mutate(
      { itemId: letterhead.ItemId, input: { Name: editName.trim() } },
      { onSuccess: () => setShowEditDialog(false) }
    );
  };

  const handleUpdateContentArea = () => {
    onUpdate.mutate(
      {
        itemId: letterhead.ItemId,
        input: {
          ContentAreaX: Math.round(contentArea.x),
          ContentAreaY: Math.round(contentArea.y),
          ContentAreaWidth: Math.round(contentArea.width),
          ContentAreaHeight: Math.round(contentArea.height),
        },
      },
      { onSuccess: () => setShowContentAreaDialog(false) }
    );
  };

  // Content area drag handling — accepts both mouse and touch so the
  // resize handles work on phones and tablets. Tracks both axes so the
  // east/west handles can adjust horizontal margins too.
  const startDrag = (clientX: number, clientY: number, handle: string) => {
    setIsDragging(true);
    setDragHandle(handle);
    setDragStart({ x: clientX, y: clientY });
    setInitialArea(contentArea);
  };

  const handleMouseDown = (e: React.MouseEvent, handle: string) => {
    e.preventDefault();
    e.stopPropagation();
    startDrag(e.clientX, e.clientY, handle);
  };

  const handleTouchStart = (e: React.TouchEvent, handle: string) => {
    e.stopPropagation();
    startDrag(e.touches[0].clientX, e.touches[0].clientY, handle);
  };

  useEffect(() => {
    if (!isDragging || !initialArea) return;

    const moveTo = (clientX: number, clientY: number) => {
      const deltaX = (clientX - dragStart.x) / scale;
      const deltaY = (clientY - dragStart.y) / scale;
      const newArea = { ...initialArea };

      if (dragHandle === 'n') {
        const newY = Math.max(
          0,
          Math.min(initialArea.y + deltaY, initialArea.y + initialArea.height - 50)
        );
        newArea.height = initialArea.y + initialArea.height - newY;
        newArea.y = newY;
      } else if (dragHandle === 's') {
        newArea.height = Math.max(
          50,
          Math.min(initialArea.height + deltaY, letterhead.PageHeight - initialArea.y)
        );
      } else if (dragHandle === 'w') {
        const newX = Math.max(
          0,
          Math.min(initialArea.x + deltaX, initialArea.x + initialArea.width - 50)
        );
        newArea.width = initialArea.x + initialArea.width - newX;
        newArea.x = newX;
      } else if (dragHandle === 'e') {
        newArea.width = Math.max(
          50,
          Math.min(initialArea.width + deltaX, letterhead.PageWidth - initialArea.x)
        );
      }

      setContentArea(newArea);
    };

    const handleMouseMove = (e: MouseEvent) => moveTo(e.clientX, e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      // Prevent page scroll while dragging the handle.
      e.preventDefault();
      moveTo(e.touches[0].clientX, e.touches[0].clientY);
    };
    const handleEnd = () => {
      setIsDragging(false);
      setDragHandle(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
    document.addEventListener('touchcancel', handleEnd);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      document.removeEventListener('touchcancel', handleEnd);
    };
  }, [
    isDragging,
    dragHandle,
    dragStart,
    initialArea,
    scale,
    letterhead.PageWidth,
    letterhead.PageHeight,
  ]);

  const scaledArea = {
    x: contentArea.x * scale,
    y: contentArea.y * scale,
    width: contentArea.width * scale,
    height: contentArea.height * scale,
  };

  return (
    <>
      <div
        className={`group relative border-2 rounded-lg overflow-hidden bg-card transition-all ${
          letterhead.IsDefault
            ? 'border-selise-blue ring-2 ring-selise-blue/20'
            : 'border-transparent hover:border-border'
        }`}
      >
        {/* Thumbnail */}
        <div className="relative aspect-8.5/11 bg-muted/50">
          <img
            src={letterhead.FileUrl}
            alt={letterhead.Name}
            className="w-full h-full object-contain"
          />
          {letterhead.IsDefault && (
            <div className="absolute top-2 left-2 flex items-center gap-1.5 px-2.5 py-1 bg-selise-blue text-white text-xs font-medium rounded-full shadow-sm">
              <Check className="w-3 h-3" />
              {t('ORGSETTINGS_LETTERHEAD_BADGEDEFAULT')}
            </div>
          )}
        </div>

        {/* Info */}
        <div className="p-3">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-sm text-[hsl(var(--fg))] truncate pr-2">
              {letterhead.Name}
            </h3>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 w-8 p-0 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
                  disabled={isUpdating || isDeleting}
                >
                  {isUpdating || isDeleting ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <MoreVertical className="h-4 w-4" />
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => setShowEditDialog(true)}>
                  <Pencil className="w-4 h-4 mr-2" />
                  {t('ORGSETTINGS_LETTERHEAD_MENU_RENAME')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setShowContentAreaDialog(true)}>
                  <Move className="w-4 h-4 mr-2" />
                  {t('ORGSETTINGS_LETTERHEAD_MENU_EDITCONTENTAREA')}
                </DropdownMenuItem>
                {!letterhead.IsDefault && (
                  <DropdownMenuItem onClick={handleSetDefault}>
                    <Star className="w-4 h-4 mr-2" />
                    {t('ORGSETTINGS_LETTERHEAD_MENU_SETDEFAULT')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-crimson focus:text-crimson"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('ORGSETTINGS_LETTERHEAD_MENU_DELETE')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <p className="text-xs text-muted-foreground mt-1 truncate">{letterhead.FileName}</p>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="w-[95vw] max-w-md p-4 sm:p-6 rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ORGSETTINGS_LETTERHEAD_DELETEDIALOG_TITLE')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('ORGSETTINGS_LETTERHEAD_DELETEDIALOG_DESCRIPTION', { name: letterhead.Name })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel disabled={isDeleting} className="w-full sm:w-auto mt-0">
              {t('ORGSETTINGS_LETTERHEAD_ACTIONS_CANCEL')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={isDeleting}
              className="w-full sm:w-auto bg-crimson hover:bg-crimson/90"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('ORGSETTINGS_LETTERHEAD_ACTIONS_DELETING')}
                </>
              ) : (
                t('ORGSETTINGS_LETTERHEAD_ACTIONS_DELETE')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Name Dialog */}
      <AlertDialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <AlertDialogContent className="w-[95vw] max-w-md p-4 sm:p-6 rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('ORGSETTINGS_LETTERHEAD_RENAMEDIALOG_TITLE')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('ORGSETTINGS_LETTERHEAD_RENAMEDIALOG_DESCRIPTION')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-2 sm:py-4">
            <Label htmlFor="editName" className="sr-only">
              {t('ORGSETTINGS_LETTERHEAD_RENAMEDIALOG_NAMELABEL')}
            </Label>
            <Input
              id="editName"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              placeholder={t('ORGSETTINGS_LETTERHEAD_RENAMEDIALOG_NAMEPLACEHOLDER')}
              autoFocus
            />
          </div>
          <AlertDialogFooter className="flex-col-reverse sm:flex-row gap-2">
            <AlertDialogCancel
              disabled={isUpdating}
              onClick={() => setEditName(letterhead.Name)}
              className="w-full sm:w-auto mt-0"
            >
              {t('ORGSETTINGS_LETTERHEAD_ACTIONS_CANCEL')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleUpdateName}
              disabled={isUpdating || !editName.trim()}
              className="w-full sm:w-auto"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('ORGSETTINGS_LETTERHEAD_ACTIONS_SAVING')}
                </>
              ) : (
                t('ORGSETTINGS_LETTERHEAD_ACTIONS_SAVE')
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Content Area Dialog */}
      <Dialog open={showContentAreaDialog} onOpenChange={setShowContentAreaDialog}>
        <DialogContent className="w-[95vw] max-w-6xl! max-h-[90dvh] overflow-y-auto p-4 sm:p-6 rounded-2xl">
          <DialogHeader>
            <DialogTitle>{t('ORGSETTINGS_LETTERHEAD_CONTENTAREADIALOG_TITLE')}</DialogTitle>
            <DialogDescription>
              {t('ORGSETTINGS_LETTERHEAD_CONTENTAREADIALOG_DESCRIPTION', {
                name: letterhead.Name,
              })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="relative rounded-lg bg-muted/30 p-2 md:p-4 flex items-center justify-center min-h-[280px] md:min-h-[450px]">
              <div
                ref={imageContainerRef}
                className="relative shadow-lg"
                style={{
                  width: letterhead.PageWidth * scale,
                  height: letterhead.PageHeight * scale,
                }}
              >
                <img
                  src={letterhead.FileUrl}
                  alt={letterhead.Name}
                  className="block rounded"
                  style={{
                    width: letterhead.PageWidth * scale,
                    height: letterhead.PageHeight * scale,
                  }}
                  draggable={false}
                  onLoad={() => setImageLoaded(true)}
                />

                {/* Content area overlay */}
                {imageLoaded && (
                  <div
                    className="absolute border-2 border-selise-blue bg-selise-blue/10 pointer-events-none"
                    style={{
                      left: scaledArea.x,
                      top: scaledArea.y,
                      width: scaledArea.width,
                      height: scaledArea.height,
                    }}
                  >
                    {/* Top handle */}
                    <div
                      className="absolute -top-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-selise-blue rounded cursor-ns-resize flex items-center justify-center pointer-events-auto hover:bg-selise-blue/80 transition-colors touch-none"
                      onMouseDown={(e) => handleMouseDown(e, 'n')}
                      onTouchStart={(e) => handleTouchStart(e, 'n')}
                    >
                      <div className="w-10 h-0.5 bg-white/80 rounded" />
                    </div>
                    {/* Bottom handle */}
                    <div
                      className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-20 h-6 bg-selise-blue rounded cursor-ns-resize flex items-center justify-center pointer-events-auto hover:bg-selise-blue/80 transition-colors touch-none"
                      onMouseDown={(e) => handleMouseDown(e, 's')}
                      onTouchStart={(e) => handleTouchStart(e, 's')}
                    >
                      <div className="w-10 h-0.5 bg-white/80 rounded" />
                    </div>
                    {/* Left handle */}
                    <div
                      className="absolute top-1/2 -left-3 -translate-y-1/2 w-6 h-20 bg-selise-blue rounded cursor-ew-resize flex items-center justify-center pointer-events-auto hover:bg-selise-blue/80 transition-colors touch-none"
                      onMouseDown={(e) => handleMouseDown(e, 'w')}
                      onTouchStart={(e) => handleTouchStart(e, 'w')}
                    >
                      <div className="h-10 w-0.5 bg-white/80 rounded" />
                    </div>
                    {/* Right handle */}
                    <div
                      className="absolute top-1/2 -right-3 -translate-y-1/2 w-6 h-20 bg-selise-blue rounded cursor-ew-resize flex items-center justify-center pointer-events-auto hover:bg-selise-blue/80 transition-colors touch-none"
                      onMouseDown={(e) => handleMouseDown(e, 'e')}
                      onTouchStart={(e) => handleTouchStart(e, 'e')}
                    >
                      <div className="h-10 w-0.5 bg-white/80 rounded" />
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
              <div>
                {t('ORGSETTINGS_LETTERHEAD_CONTENTAREADIALOG_HEADEREXCLUDED')}{' '}
                <span className="font-mono">{Math.round(contentArea.y)}px</span>
              </div>
              <div>
                {t('ORGSETTINGS_LETTERHEAD_CONTENTAREADIALOG_FOOTEREXCLUDED')}{' '}
                <span className="font-mono">
                  {Math.round(letterhead.PageHeight - contentArea.y - contentArea.height)}px
                </span>
              </div>
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowContentAreaDialog(false)}
                disabled={isUpdating}
                className="w-full sm:w-auto"
              >
                {t('ORGSETTINGS_LETTERHEAD_ACTIONS_CANCEL')}
              </Button>
              <Button
                size="sm"
                onClick={handleUpdateContentArea}
                disabled={isUpdating}
                className="w-full sm:w-auto"
              >
                {isUpdating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('ORGSETTINGS_LETTERHEAD_ACTIONS_SAVING')}
                  </>
                ) : (
                  t('ORGSETTINGS_LETTERHEAD_ACTIONS_SAVECHANGES')
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
